/**
 * The paths a customer can be at on a vendor's public pages (TRO-1187).
 *
 * One root per vendor: the shop is the vendor's URL, and a table they are operating today is a
 * path under it. Before this, the table was a top-level `/browse/<eventId>` with no way to tell
 * from the URL whose table it was, so a vendor had no single link that contained what they publish.
 *
 * The event id stays in the path because a vendor can operate two tables at once (ADR-0012), so
 * the slug alone cannot say which table a customer is standing at.
 *
 * These are the canonical paths in the sense that every new link is built from them — not in the
 * sense that a customer is ever moved onto them. `/browse/<eventId>` is printed on table signage
 * already in the world, keeps rendering forever, and keeps its own URL.
 */

export const shopPath = (slug: string): string => `/shop/${encodeURIComponent(slug)}`;
export const buylistPath = (slug: string): string => `${shopPath(slug)}/buylist`;

/**
 * The shop narrowed to one scanned card.
 *
 * A query parameter rather than a path segment, because ADR-0056 makes this destination a browse
 * and not a detail view: the page shows the vendor's whole published book with the scanned card
 * brought to the top. The backend builds the same shape in `shop_inventory_item_url`.
 */
export const shopItemPath = (slug: string, itemId: string): string =>
  `${shopPath(slug)}?item=${encodeURIComponent(itemId)}`;

/** One card's page on the shop, where a customer compares its price (`usePublicShopItem`). */
export const shopCardPath = (slug: string, itemId: string): string =>
  `${shopPath(slug)}/item/${encodeURIComponent(itemId)}`;

export const tablePath = (slug: string, eventId: string): string =>
  `${shopPath(slug)}/table/${encodeURIComponent(eventId)}`;

/** One card at a table (#341), with the table's pick list; a shop card is `shopCardPath`. */
export const tableItemPath = (slug: string, eventId: string, itemId: string): string =>
  `${tablePath(slug, eventId)}/${encodeURIComponent(itemId)}`;
